import { useState, useEffect } from 'react';
import axios from 'axios';

export const useAdvertiserAccess = () => {
  const [allUsersList, setAllUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersListError, setUsersListError] = useState('');
  const [invitedUserExistingAccess, setInvitedUserExistingAccess] = useState([]);
  const [movedFromExisting, setMovedFromExisting] = useState([]);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    fetchAllUsersList();
  }, []);

  const setInvitedUserAccess = (email) => {
    if (!email || email.trim() === '') {
      setInvitedUserExistingAccess([]);
      return;
    }

    if (allUsersList.length > 0) {
      const user = allUsersList.find((user) => user.email_id === email);
      if (user) {
        const existingAccess = [...(user.advertisers_utc || []), ...(user.advertisers_non_utc || [])];
        setInvitedUserExistingAccess(existingAccess);
      } else {
        setInvitedUserExistingAccess([]);
      }
    }
  };

  const cleanJsonData = (jsonString) => {
    try {
      let cleaned = jsonString.replace(/:\s*NaN/g, ': null');
      cleaned = cleaned.replace(/:\s*"NaN"/g, ': null');
      cleaned = cleaned.replace(/:\s*undefined/g, ': null');
      cleaned = cleaned.replace(/:\s*"undefined"/g, ': null');
      cleaned = cleaned.replace(/:\s*""/g, ': null');
      cleaned = cleaned.replace(/,(\s*[}\]])/g, '$1');
      return cleaned;
    } catch (error) {
      console.error('Error cleaning JSON data:', error);
      return jsonString;
    }
  };

  const fetchAllUsersList = async () => {
    try {
      setLoadingUsers(true);
      setUsersListError('');

      const response = await axios.post(
        '/get_all_users_list',
        {},
        {
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.data) {
        try {
          let cleanData = cleanJsonData(response.data.data);

          const usersData = JSON.parse(cleanData);
          setAllUsersList(usersData);
          setUsersListError('');
        } catch (parseError) {
          console.error('JSON parsing error:', parseError);
          setUsersListError(`Failed to parse API response. Data format is invalid.`);
        }
      } else {
        setUsersListError('No data received from API');
      }
    } catch (error) {
      console.error('Error fetching users list:', error);

      if (error.code === 'ECONNABORTED') {
        setUsersListError('Request timed out. Please check your connection and try again.');
      } else if (error.code === 'ERR_NETWORK') {
        setUsersListError('Network error. Please check if the backend server is running.');
      } else if (error.code === 'ECONNREFUSED') {
        setUsersListError('Cannot connect to server. Please check if backend is running.');
      } else if (error.response?.status === 404) {
        setUsersListError('API endpoint not found. Please check backend configuration.');
      } else if (error.response?.status === 500) {
        setUsersListError('Server error. Please try again later.');
      } else if (error.message === 'Network Error') {
        setUsersListError('Network error. Please check if the backend server is running and accessible.');
      } else {
        setUsersListError(`Failed to load users list: ${error.message}`);
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchOrgDetails = async () => {
    try {
      const response = await fetch('/get_org_details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data) {
          return data.data;
        }
      }
      return [];
    } catch (error) {
      console.error('Error fetching organizations:', error);
      return [];
    }
  };

  const retryFetch = () => {
    if (retryCount < 3) {
      setRetryCount((prev) => prev + 1);
      setTimeout(() => {
        fetchAllUsersList();
      }, Math.pow(2, retryCount) * 1000);
    } else {
      setUsersListError('Failed to load after multiple attempts. Please check your connection and refresh the page.');
    }
  };

  useEffect(() => {
    fetchAllUsersList();
  }, []);

  useEffect(() => {
    if (allUsersList.length > 0) {
      setRetryCount(0);
    }
  }, [allUsersList]);

  return {
    allUsersList,
    loadingUsers,
    usersListError,
    invitedUserExistingAccess,
    movedFromExisting,
    setMovedFromExisting,
    setInvitedUserAccess,
    retryFetch,
    fetchAllUsersList,
    fetchOrgDetails
  };
};
